import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Button, Heading, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Text, VStack, HStack, Spinner, useToast, Input, Select, Flex
} from "@chakra-ui/react";
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const ManualReviewResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOutcome, setFilterOutcome] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [configIdFilter, setConfigIdFilter] = useState('');
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (accessToken) {
      fetchResults();
    }
  }, [accessToken]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      let url = '/api/manual-coding/reviewed-cases';
      if (configIdFilter) {
        url += `?config_id=${configIdFilter}`;
      }
      const response = await callApi(url, accessToken);
      const data = await response.json();
      if (response.ok) {
        setResults(data);
      } else {
        throw new Error(data.error || 'Failed to fetch reviewed cases');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = useMemo(() => {
    return results.filter(item => {
      // Filter by Outcome
      if (filterOutcome !== 'all' && item.review_outcome !== filterOutcome) {
        return false;
      }
      
      // Filter by Search Term (Case ID or Reviewer)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesId = item.external_case_id?.toLowerCase().includes(term);
        const matchesReviewer = item.reviewer_name?.toLowerCase().includes(term);
        if (!matchesId && !matchesReviewer) {
          return false;
        }
      }
      
      return true;
    });
  }, [results, filterOutcome, searchTerm]);

  const getOutcomeBadge = (outcome) => {
    if (outcome === 'revedy_correct') {
      return <Badge colorScheme="green">Revedy Correct</Badge>;
    }
    if (outcome === 'manual_correct') {
      return <Badge colorScheme="purple">Manual Correct</Badge>;
    }
    return <Badge>{outcome}</Badge>;
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8}>
        <Flex justify="space-between" align="center" mb={6}>
          <HStack>
            <Button leftIcon={<ArrowBackIcon />} onClick={() => navigate('/cpt-feedback-dashboard')}>
              Back to Dashboard
            </Button>
            <Heading size="lg" ml={4}>Review Results Analysis</Heading>
          </HStack>
        </Flex>

        <Box bg="white" p={4} borderRadius="lg" borderWidth="1px" mb={6}>
          <VStack align="start" spacing={4}>
            <HStack spacing={4} align="end">
                <Box minW="200px">
                    <Text mb={1} fontSize="sm" fontWeight="bold">Filter by Config ID</Text>
                    <Input
                        placeholder="Enter Configuration ID"
                        value={configIdFilter}
                        onChange={(e) => setConfigIdFilter(e.target.value)}
                        size="sm"
                    />
                </Box>
                <Button onClick={fetchResults} size="sm" colorScheme="blue">
                    Apply Config Filter
                </Button>
            </HStack>

            <HStack spacing={4} width="100%">
                <Box minW="200px">
                <Text mb={1} fontSize="sm" fontWeight="bold">Filter by Outcome</Text>
                <Select 
                    value={filterOutcome} 
                    onChange={(e) => setFilterOutcome(e.target.value)}
                    size="sm"
                >
                    <option value="all">All Outcomes</option>
                    <option value="revedy_correct">Revedy Correct</option>
                    <option value="manual_correct">Manual Correct</option>
                </Select>
                </Box>
                <Box minW="300px">
                <Text mb={1} fontSize="sm" fontWeight="bold">Search</Text>
                <Input 
                    placeholder="Search Case ID or Reviewer..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="sm"
                />
                </Box>
                <Box pt={6}>
                    <Text fontSize="sm" color="gray.500">
                        Showing {filteredResults.length} of {results.length} reviews
                    </Text>
                </Box>
            </HStack>
          </VStack>
        </Box>

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <Box overflowX="auto" borderWidth="1px" borderRadius="lg">
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Case ID</Th>
                  <Th>Review Date</Th>
                  <Th>Reviewer</Th>
                  <Th>Outcome</Th>
                  <Th>Revedy Codes</Th>
                  <Th>Manual Codes</Th>
                  <Th width="30%">Notes</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredResults.map((row) => (
                  <Tr key={row.case_id}>
                    <Td fontWeight="bold">{row.external_case_id}</Td>
                    <Td>{new Date(row.review_date).toLocaleDateString()} {new Date(row.review_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</Td>
                    <Td>{row.reviewer_name}</Td>
                    <Td>{getOutcomeBadge(row.review_outcome)}</Td>
                    <Td>
                        <Text color="blue.600" fontWeight="medium">
                            {row.ai_codes.join(', ') || '-'}
                        </Text>
                    </Td>
                    <Td>
                        <Text color="purple.600" fontWeight="medium">
                            {row.manual_codes.join(', ') || '-'}
                        </Text>
                    </Td>
                    <Td>
                        <Text noOfLines={2} title={row.note_text}>
                            {row.note_text}
                        </Text>
                    </Td>
                    <Td>
                        <Button size="xs" onClick={() => navigate(`/cpt-feedback-review/${row.case_id}`)}>
                            View
                        </Button>
                    </Td>
                  </Tr>
                ))}
                {filteredResults.length === 0 && (
                    <Tr>
                        <Td colSpan={8} textAlign="center" py={4}>
                            No results found matching your filters.
                        </Td>
                    </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ManualReviewResults;